import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Droplets, AlertTriangle, Calendar, ChevronRight, Activity } from 'lucide-react';

export default function IAPredictionStage() {
  return (
    <section className="py-24 lg:py-40 bg-white relative overflow-hidden font-sans">
      {/* Architectural Background */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-[600px] bg-gradient-to-b from-slate-50 to-transparent" />
        <div className="absolute inset-0 opacity-[0.4]" style={{ backgroundImage: `radial-gradient(circle at 1px 1px, #e2e8f0 1.5px, transparent 0)`, backgroundSize: '48px 48px' }} />
        <div className="absolute top-[20%] right-[-10%] w-[60%] h-[60%] bg-indigo-50 blur-[120px] rounded-full opacity-[0.6]" />
      </div>

      <div className="container mx-auto px-6 max-w-[1400px] relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-32">
          
          {/* Text Content */}
          <div className="lg:col-span-12 xl:col-span-5 flex flex-col items-start text-left max-w-xl">
             <motion.div
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="flex items-center gap-2 mb-10"
              >
                <div className="p-1.5 rounded-lg bg-indigo-50 text-indigo-600 shadow-sm border border-indigo-100">
                   <Activity size={12} strokeWidth={3} />
                </div>
                <span className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-400">Previsão de Demanda</span>
             </motion.div>

             <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-4xl lg:text-6xl font-black text-slate-950 tracking-tight leading-[1.05] mb-8"
              >
                O futuro do seu estoque <br />
                <span className="text-indigo-600 text-3xl lg:text-5xl font-light italic">previsto pelo LukIA.</span>
             </motion.h2>
             
             <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-slate-500 text-lg lg:text-xl font-medium leading-relaxed mb-12"
              >
                Nosso algoritmo analisa picos de venda, feriados e tendências de mercado para prever exatamente quando seus tanques ficarão vazios, evitando perdas e imprevistos.
             </motion.p>

             <div className="grid grid-cols-2 gap-8 w-full">
                {[
                  { label: 'Precisão', value: '98.7%', icon: <Activity size={16}/> },
                  { label: 'Preditividade', value: '15 dias', icon: <Calendar size={16}/> }
                ].map((stat, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 + (i * 0.1) }}
                    className="p-6 rounded-[2rem] bg-slate-50/50 border border-slate-100 backdrop-blur-sm"
                  >
                     <div className="text-indigo-600 mb-3">{stat.icon}</div>
                     <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">{stat.label}</p>
                     <p className="text-2xl font-black text-slate-950">{stat.value}</p>
                  </motion.div>
                ))}
             </div>
          </div>

          {/* Visual stage: Predictive Graph Mockup */}
          <div className="lg:col-span-12 xl:col-span-7 w-full flex items-center justify-center">
             <div className="relative w-full max-w-[700px] aspect-[4/3] group">
                
                {/* Main Graph Card */}
                <motion.div 
                   initial={{ opacity: 0, scale: 0.95 }}
                   whileInView={{ opacity: 1, scale: 1 }}
                   viewport={{ once: true }}
                   className="relative z-20 w-full h-full bg-white rounded-[3rem] shadow-[0_48px_100px_-24px_rgba(0,0,0,0.12)] border border-slate-100 overflow-hidden p-10 flex flex-col"
                >
                   <div className="flex items-center justify-between mb-10">
                      <div>
                         <h4 className="text-xl font-black text-slate-900 tracking-tight">Tanque Principal 01</h4>
                         <p className="text-sm text-slate-400 font-bold uppercase tracking-widest">Gasolina Comum • 30k Litros</p>
                      </div>
                      <div className="px-4 py-2 bg-indigo-50 rounded-xl text-indigo-600 font-black text-[10px] uppercase tracking-widest border border-indigo-100">
                         Análise Preditiva
                      </div>
                   </div>

                   {/* Mockup Graph Grid */}
                   <div className="flex-1 relative border-l border-b border-slate-100">
                      {/* Grid Lines */}
                      <div className="absolute inset-0 flex flex-col justify-between py-2">
                         {[...Array(5)].map((_, i) => <div key={i} className="w-full h-px bg-slate-50" />)}
                      </div>

                      {/* Animated Prediction Path */}
                      <svg className="absolute inset-0 w-full h-full overflow-visible" viewBox="0 0 500 300" preserveAspectRatio="none">
                         {/* Static Historical Path */}
                         <motion.path 
                            d="M 0,50 L 50,70 L 100,60 L 150,90 L 200,80 L 250,110" 
                            fill="none" stroke="#94a3b8" strokeWidth="3" strokeLinecap="round" 
                         />
                         {/* Animated Prediction Path */}
                         <motion.path 
                            initial={{ pathLength: 0, opacity: 0 }}
                            whileInView={{ pathLength: 1, opacity: 1 }}
                            transition={{ duration: 1.5, ease: "easeInOut", delay: 1 }}
                            d="M 250,110 L 300,150 L 350,180 L 400,240 L 450,280" 
                            fill="none" stroke="#4f46e5" strokeWidth="4" strokeLinecap="round" strokeDasharray="12 8" 
                         />
                         {/* Indicators */}
                         <motion.circle 
                           initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ delay: 2 }}
                           cx="450" cy="280" r="8" fill="#ef4444" 
                         />
                         <motion.circle 
                           initial={{ scale: 0 }} whileInView={{ scale: 1 }} transition={{ delay: 2.2 }}
                           cx="450" cy="280" r="16" fill="#ef4444" opacity="0.2"
                         />
                      </svg>

                      {/* Labels on graph */}
                      <div className="absolute top-[30%] left-[45%] bg-white/90 backdrop-blur-md p-3 rounded-xl shadow-lg border border-slate-100 flex items-center gap-3">
                         <div className="p-1.5 rounded-md bg-indigo-600 text-white"><AlertTriangle size={14}/></div>
                         <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase leading-none mb-1">Esvaziamento em</p>
                            <p className="text-xs font-black text-slate-900 leading-none">04 DIAS ÚTEIS</p>
                         </div>
                      </div>
                   </div>

                   <div className="mt-10 pt-10 border-t border-slate-50 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                         <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400">
                            <Droplets size={24}/>
                         </div>
                         <div>
                            <p className="text-xs font-black text-slate-900">Litros Atuais</p>
                            <p className="text-sm font-bold text-slate-400">8.450 / 30.000 L</p>
                         </div>
                      </div>
                      <button className="flex items-center gap-2 group text-indigo-600 font-black text-xs uppercase tracking-widest">
                         Solicitar Compra <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                      </button>
                   </div>
                </motion.div>

                {/* Sub-Card: Order Recommendation */}
                <motion.div 
                   initial={{ opacity: 0, x: 20 }}
                   whileInView={{ opacity: 1, x: 0 }}
                   transition={{ delay: 1.5 }}
                   className="absolute -right-12 bottom-20 z-30 bg-slate-900 text-white p-6 rounded-[2rem] shadow-2xl max-w-[240px] border border-white/10"
                >
                   <div className="w-10 h-10 rounded-xl bg-purple-500 flex items-center justify-center mb-4 text-white">
                      <TrendingUp size={20}/>
                   </div>
                   <h5 className="font-black text-sm mb-2 text-white/90">Insight de Compra</h5>
                   <p className="text-xs font-medium text-white/50 leading-relaxed">
                      LUK recomenda solicitar abastecimento amanhã de manhã para garantir o melhor preço da distribuidora.
                   </p>
                </motion.div>
             </div>
          </div>

        </div>
      </div>
    </section>
  );
}
